import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from '../../../_metronic/helpers'
import Select, {
  StylesConfig,
  MultiValue,
  SingleValue,
  components,
  MultiValueRemoveProps,
} from 'react-select'
import { getEntityMeta, useT } from '_metronic/i18n/Metronici18n'

export type SelectValue = string | number | boolean | null

export type IOption<TValue = SelectValue> = {
  label: string
  value: TValue
  /** Opção visível mas não selecionável (lida nativamente pelo react-select). */
  isDisabled?: boolean
}

const SELECT_ALL_OPTION: IOption = {
  label: 'Selecionar Todos',
  value: 'selectAll',
}

type ICustomSelect = {
  onChange?: (value: any) => void // FIX type any
  onBlur?: (value: any) => void // FIX type any
  placeholder?: string
  label?: string
  value?: IOption | IOption[]
  isMulti?: boolean
  hideSelectAll?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  isClearable?: boolean
  isInvalid?: boolean
  customStyles?: StylesConfig<IOption>
  className?: string
  name?: string
  showMultiValueRemove?: boolean
  fetchOptions?: (
    inputValue: string,
    selectAll: boolean,
    extraParams?: Object,
    page?: number
  ) => Promise<{ options: IOption[]; hasMore: boolean; rawData?: any[] }>
  options?: IOption[]
  fetchOptionsParams?: Object
  autoSelectSingle?: boolean
  onOptionsLoaded?: (data: any[]) => void
  entity?: string
  /** Id do input interno do react-select, para associar um <label htmlFor>. */
  inputId?: string
  /** Nome acessível quando o select não tem <label> visível associado (ex.: linhas repetidas). */
  ariaLabel?: string
  /**
   * O `fetchOptions` já filtra pelo texto digitado, inclusive em campos fora do rótulo (patrimônio,
   * CNPJ): desliga o filtro do react-select por rótulo, que esconderia resultados válidos. Opt-in:
   * boa parte dos `fetchOptions` devolve a lista inteira e depende desse filtro (UF, gênero, meses).
   */
  filtraNoServidor?: boolean
}

export const CustomSelect: FC<ICustomSelect> = ({
  onChange,
  onBlur,
  placeholder,
  value,
  isMulti = false,
  hideSelectAll = false,
  customStyles,
  className = '',
  isDisabled = false,
  isRequired = false,
  isClearable = true,
  isInvalid,
  name: label = '',
  showMultiValueRemove = true,
  fetchOptions,
  options: syncOptions,
  fetchOptionsParams,
  autoSelectSingle,
  onOptionsLoaded,
  entity,
  inputId,
  ariaLabel,
  filtraNoServidor,
}) => {
  const { t } = useT()
  const meta = entity ? getEntityMeta(t, entity) : null

  const [options, setOptions] = useState<IOption[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [falhaAoCarregar, setFalhaAoCarregar] = useState(false)
  const [internalValue, setInternalValue] = useState<
    MultiValue<IOption> | SingleValue<IOption> | null
  >(value ?? null)

  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const debouncedInput = useDebounce(inputValue, 300) ?? ''
  const fetchOptionsParamsKey = JSON.stringify(fetchOptionsParams ?? null)
  const fetchOptionsParamsRef = useRef(fetchOptionsParams)

  fetchOptionsParamsRef.current = fetchOptionsParams

  // Refs estáveis para os callbacks/props que o form pai recria a cada render
  // (onChange/fetchOptions/onOptionsLoaded/syncOptions). Lê-los via `.current` impede
  // que mudanças de identidade dessas funções re-disparem o efeito de carregamento de
  // opções a cada tecla digitada no formulário. Mesmo padrão de `fetchOptionsParamsRef`.
  const fetchOptionsRef = useRef(fetchOptions)
  const onOptionsLoadedRef = useRef(onOptionsLoaded)
  const onChangeRef = useRef(onChange)
  const syncOptionsRef = useRef(syncOptions)
  const valueRef = useRef(value)

  fetchOptionsRef.current = fetchOptions
  onOptionsLoadedRef.current = onOptionsLoaded
  onChangeRef.current = onChange
  syncOptionsRef.current = syncOptions
  valueRef.current = value

  // Ignora respostas fora de ordem: cada chamada de loadOptions recebe um id
  // incremental e só a última requisição disparada pode aplicar seu resultado.
  const loadOptionsRequestIdRef = useRef(0)

  const resolvedPlaceholder =
    placeholder ??
    t(
      isMulti ? 'placeholder.select.multiple' : 'placeholder.select',
      {
        name: meta?.labelLower,
        gender: meta?.gender,
      },
      'Selecione...'
    )

  const extendedOptions = useMemo(
    () => (isMulti && !hideSelectAll ? [SELECT_ALL_OPTION, ...options] : [...options]),
    [hideSelectAll, isMulti, options]
  )

  const handleChange = useCallback(
    async (selected: MultiValue<IOption> | SingleValue<IOption>) => {
      setInternalValue(selected)

      if (!isMulti) {
        onChangeRef.current?.(selected)
        return
      }

      if (Array.isArray(selected) && selected.length === 0) {
        setSelectAll(false)
        onChangeRef.current?.([])
        return
      }

      if (Array.isArray(selected) && selected.some((opt) => opt.value === 'selectAll')) {
        if (!selectAll) {
          try {
            setLoading(true)
            const { options: allOptions } = (await fetchOptionsRef.current?.(
              debouncedInput,
              true,
              fetchOptionsParamsRef.current,
              0
            )) || {
              options: syncOptionsRef.current || [],
            }
            setOptions(allOptions)
            setSelectAll(true)
            onChangeRef.current?.(allOptions)
          } catch (error) {
            console.error('Erro ao pegar todas opcoes', error)
          } finally {
            setLoading(false)
          }
        }
      } else {
        setSelectAll(false)
        onChangeRef.current?.(selected)
      }
    },
    [debouncedInput, isMulti, selectAll]
  )

  const MultiValueRemove = (props: MultiValueRemoveProps<any, boolean>) => {
    return showMultiValueRemove ? <components.MultiValueRemove {...props} /> : null
  }

  const loadOptions = useCallback(
    async (reset = true, newPage = 0) => {
      const currentFetchOptions = fetchOptionsRef.current
      const currentSyncOptions = syncOptionsRef.current

      if (currentFetchOptions && !currentSyncOptions) {
        const requestId = ++loadOptionsRequestIdRef.current
        setLoading(true)
        setFalhaAoCarregar(false)
        try {
          let pageToFetch = newPage
          let isReset = reset

          while (true) {
            const {
              options: fetchedOptions,
              hasMore,
              rawData,
            } = await currentFetchOptions(
              debouncedInput,
              selectAll,
              fetchOptionsParamsRef.current,
              pageToFetch
            )

            // Descarta respostas fora de ordem: uma requisição mais nova já foi
            // disparada, então este resultado está obsoleto.
            if (requestId !== loadOptionsRequestIdRef.current) return

            const substitui = isReset
            setOptions((prev) => (substitui ? fetchedOptions : [...prev, ...fetchedOptions]))
            setHasMore(hasMore)
            setPage(pageToFetch)

            if (rawData) onOptionsLoadedRef.current?.(rawData)

            if (autoSelectSingle && fetchedOptions.length === 1) {
              const singleOption = fetchedOptions[0]
              if (!isMulti) {
                handleChange?.(singleOption)
              } else {
                handleChange?.([singleOption])
              }
            }

            if (!isMulti || !hasMore || fetchedOptions.length === 0) break

            const selectedValues = new Set(
              (Array.isArray(valueRef.current) ? valueRef.current : []).map((opt) => opt?.value)
            )
            const hasSelectableOption = fetchedOptions.some((opt) => !selectedValues.has(opt.value))
            if (hasSelectableOption) break

            pageToFetch += 1
            isReset = false
          }
        } catch (error) {
          if (requestId !== loadOptionsRequestIdRef.current) return
          console.error('Error fetching options:', error)
          setFalhaAoCarregar(true)
        } finally {
          // Só a última requisição limpa o loading; requisições obsoletas não
          // apagam o estado de carregamento da mais recente ainda em andamento.
          if (requestId === loadOptionsRequestIdRef.current) setLoading(false)
        }
      } else if (currentSyncOptions) {
        setOptions(currentSyncOptions)
      } else {
        setOptions([])
      }
    },
    [autoSelectSingle, debouncedInput, handleChange, isMulti, selectAll]
  )

  // Efeito de carregamento: reage apenas às entradas reais da busca
  // (debouncedInput, selectAll via loadOptions) e a mudanças de fetchOptionsParams
  // (fetchOptionsParamsKey). Não depende mais das identidades de fetchOptions/onChange/
  // onOptionsLoaded, que o form pai recria a cada tecla — evitando refetch por tecla.
  useEffect(() => {
    loadOptions()
  }, [loadOptions, fetchOptionsParamsKey])

  // Mantém a reatividade de selects síncronos (que passam `options` em vez de
  // `fetchOptions`): quando a prop `options` muda de identidade/conteúdo, ressincroniza.
  useEffect(() => {
    if (syncOptions) {
      setOptions(syncOptions)
    }
  }, [syncOptions])

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue)
  }

  // Falha na busca não é lista vazia: digitar de novo dispara nova tentativa.
  const customNoOptionsMessage = () =>
    falhaAoCarregar
      ? 'Não foi possível carregar as opções. Digite para tentar novamente.'
      : 'Sem Opções'

  useEffect(() => {
    setInternalValue(value ?? null)
  }, [value])

  const onMenuScrollToBottom = () => {
    if (loading || !hasMore) return
    loadOptions(false, page + 1)
  }

  return (
    <Select
      name={label}
      inputId={inputId}
      aria-label={ariaLabel}
      isClearable={isClearable}
      className={`${className}`}
      classNamePrefix='react-select'
      placeholder={resolvedPlaceholder}
      onChange={handleChange}
      onBlur={onBlur}
      options={extendedOptions}
      filterOption={filtraNoServidor ? null : undefined}
      value={value ?? internalValue ?? null}
      isMulti={isMulti}
      isLoading={loading}
      isDisabled={isDisabled}
      onInputChange={handleInputChange}
      noOptionsMessage={customNoOptionsMessage}
      onMenuScrollToBottom={onMenuScrollToBottom}
      components={{
        MultiValueRemove,
      }}
      required={isRequired}
      styles={{
        control: (provided: any, state: any) => {
          // Mesmos tokens usados pelos inputs nativos (is-invalid em
          // _forms.custom.scss): erro em vermelho prevalece sobre foco/hover,
          // senão o anel de foco padrão da marca (WCAG 1.4.11/2.4.7).
          const borderColor = isInvalid
            ? 'var(--bs-danger)'
            : state.isFocused
            ? 'var(--brand-primary)'
            : 'var(--bs-gray-600)'

          const boxShadow = isInvalid
            ? '0 0 0 0.2rem rgba(var(--bs-danger-rgb), 0.4)'
            : state.isFocused
            ? '0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.6)'
            : 'none'

          return {
            ...provided,
            width: '100%',
            height: '100%',
            border: `1px solid ${borderColor}`,
            // Mesmo arredondamento dos inputs (token do tema), em vez do 4px padrão do react-select.
            borderRadius: 'var(--bs-border-radius)',
            boxShadow,
            outline: state.isFocused ? '2px solid transparent' : provided.outline,
            outlineOffset: 2,
            padding: 4,
            backgroundColor: isDisabled ? 'var(--bs-gray-300)' : 'var(--bs-gray-100)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.7 : 1,
            '&:hover': {
              borderColor: isInvalid
                ? borderColor
                : state.isFocused
                ? 'var(--brand-primary)'
                : 'var(--bs-gray-700)',
            },
          }
        },
        menu: (provided: any) => ({
          ...provided,
          backgroundColor: 'var(--bs-gray-100)',
          zIndex: 9999,
          padding: 4,
        }),
        input: (provided: any) => ({
          ...provided,
          color: 'var(--bs-gray-700)',
        }),
        option: (provided: any, state: any) => ({
          ...provided,
          backgroundColor: state.isSelected
            ? 'var(--bs-secondary)'
            : state.isFocused
            ? 'var(--bs-gray-300)'
            : 'transparent',
          color: 'var(--bs-gray-700)',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }),
        // Placeholder e valor numa linha só, com reticências: o campo nunca muda de altura
        // pelo tamanho do texto (o rótulo do campo já diz o que ele é).
        singleValue: (provided: any) => ({
          ...provided,
          color: isDisabled ? 'var(--bs-gray-600)' : 'var(--bs-gray-700)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }),
        placeholder: (base: any) => ({
          ...base,
          color: isDisabled ? 'var(--bs-gray-600)' : 'var(--bs-gray-700)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }),
        multiValue: (provided: any) => ({
          ...provided,
          backgroundColor: 'var(--bs-gray-300)',
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          color: 'var(--bs-body)',
        }),
        ...customStyles,
      }}
    />
  )
}
